/**
 * ConfirmDialog — shared destructive/confirmation modal primitive.
 *
 * Replaces native `window.confirm()` and `window.prompt()` callsites across
 * the admin web. Native dialogs render unstyleable, can't be themed for dark
 * mode, can't host a textarea/validation, and look broken on macOS.
 *
 * Patterned after compliance.tsx "type ERASE to confirm" + bills.tsx waive-
 * with-reason flow:
 *
 *   <ConfirmDialog
 *     open={open} onOpenChange={setOpen}
 *     title="Delete bill"
 *     description="This permanently removes the bill."
 *     confirmLabel="Delete"
 *     variant="danger"
 *     onConfirm={() => mutation.mutate(id)}
 *   />
 *
 *   <ConfirmDialog
 *     open={open} onOpenChange={setOpen}
 *     title="Waive bill"
 *     description="A rationale is required for audit."
 *     confirmLabel="Waive"
 *     variant="warn"
 *     promptLabel="Reason for waiver"
 *     promptPlaceholder="e.g. Resident hardship — approved by treasurer"
 *     promptValidate={(v) => v.trim().length < 3 ? 'Please give at least 3 characters' : null}
 *     onConfirm={(reason) => waive.mutate({ id, reason })}
 *   />
 *
 *   <ConfirmDialog
 *     open={open} onOpenChange={setOpen}
 *     title="Terminate employee"
 *     description="This deactivates payroll and revokes admin access."
 *     confirmLabel="Terminate"
 *     variant="danger"
 *     typeToConfirm="TERMINATE"
 *     onConfirm={() => terminate.mutate(id)}
 *   />
 */
import * as React from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const PROMPT_MAX = 500;

export type ConfirmDialogVariant = 'danger' | 'default' | 'warn';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string | React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
  /** Optional textarea for collecting a reason/note. Confirm is disabled until validation passes. */
  promptLabel?: string;
  promptPlaceholder?: string;
  promptValidate?: (v: string) => string | null;
  /** Optional typed-confirm guard. Confirm disabled until value matches exactly. */
  typeToConfirm?: string;
  loading?: boolean;
  onConfirm: (promptValue?: string) => void | Promise<void>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  variant = 'default',
  promptLabel,
  promptPlaceholder,
  promptValidate,
  typeToConfirm,
  loading = false,
  onConfirm
}: ConfirmDialogProps): JSX.Element {
  const [promptValue, setPromptValue] = React.useState('');
  const [typedValue, setTypedValue] = React.useState('');
  const confirmRef = React.useRef<HTMLButtonElement>(null);

  // Reset fields whenever the dialog is freshly opened. Otherwise the previous
  // session's reason/typed-confirm leaks into the next destructive action.
  React.useEffect(() => {
    if (open) {
      setPromptValue('');
      setTypedValue('');
      // Focus the confirm button on open. requestAnimationFrame defers past
      // Radix's initial autoFocus on the close X.
      requestAnimationFrame(() => {
        confirmRef.current?.focus();
      });
    }
  }, [open]);

  const promptError =
    promptLabel && promptValidate ? promptValidate(promptValue) : null;
  const promptOk = promptLabel ? promptError === null : true;
  const typedOk = typeToConfirm ? typedValue === typeToConfirm : true;

  const confirmDisabled = loading || !promptOk || !typedOk;

  const confirmVariant: 'danger' | 'primary' | 'secondary' =
    variant === 'danger' ? 'danger' : variant === 'warn' ? 'secondary' : 'primary';

  const handleConfirm = async (): Promise<void> => {
    if (confirmDisabled) return;
    await onConfirm(promptLabel ? promptValue : undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-vt-2">
            {variant === 'danger' || variant === 'warn' ? (
              <AlertTriangle
                className={
                  variant === 'danger'
                    ? 'h-5 w-5 text-[color:var(--vt-alert)]'
                    : 'h-5 w-5 text-[color:var(--vt-warn)]'
                }
                strokeWidth={2}
                aria-hidden="true"
              />
            ) : null}
            {title}
          </DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        {promptLabel ? (
          <div className="space-y-vt-2 py-vt-2">
            <Label htmlFor="confirm-dialog-prompt">{promptLabel}</Label>
            <Textarea
              id="confirm-dialog-prompt"
              value={promptValue}
              onChange={(e) => setPromptValue(e.target.value)}
              placeholder={promptPlaceholder}
              maxLength={PROMPT_MAX}
              rows={4}
              aria-invalid={promptError ? true : undefined}
              aria-describedby="confirm-dialog-prompt-meta"
            />
            <div
              id="confirm-dialog-prompt-meta"
              className="flex items-center justify-between text-vt-11"
            >
              <span className={promptError ? 'text-vt-alert' : 'text-vt-text-muted'}>
                {promptError ?? ' '}
              </span>
              <span className="text-vt-text-muted">
                {promptValue.length}/{PROMPT_MAX}
              </span>
            </div>
          </div>
        ) : null}

        {typeToConfirm ? (
          <div className="space-y-vt-2 py-vt-2">
            <Label htmlFor="confirm-dialog-type">
              Type{' '}
              <code className="font-mono text-vt-text">{typeToConfirm}</code> to confirm
            </Label>
            <Input
              id="confirm-dialog-type"
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              placeholder={typeToConfirm}
              autoComplete="off"
            />
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmRef}
            variant={confirmVariant}
            disabled={confirmDisabled}
            loading={loading}
            onClick={() => {
              void handleConfirm();
            }}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
